import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Pause, Play, Trash2, MapPin, Building2, Archive, Download, Eye, X } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import ConfirmModal from '../../components/ConfirmModal';
import { adminApi } from '../../lib/api';
import { resolveAssetUrl } from '../../config';

const LocationManagement = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [total, setTotal] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, locationId: null, locationName: '' });
  const [archiveModal, setArchiveModal] = useState({ isOpen: false, locationId: null, locationName: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [qrPreview, setQrPreview] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [generatingQR, setGeneratingQR] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, [search, filterStatus]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (search) params.append('search', search);
      if (filterStatus !== '') params.append('is_active', filterStatus);

      const response = await adminApi.get(`/admin/locations?${params.toString()}`);
      
      setLocations(response.data.locations);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (locationId, currentStatus) => {
    try {
      await adminApi.patch(
        `/admin/locations/${locationId}/status?is_active=${!currentStatus}`,
        {}
      );
      fetchLocations();
    } catch (err) {
      console.error('Error toggling location status:', err);
      alert(err.response?.data?.detail || 'Failed to update location status');
    }
  };

  const openArchiveModal = (location) => {
    setArchiveModal({
      isOpen: true,
      locationId: location.id,
      locationName: location.name
    });
    setErrorMessage('');
  };

  const closeArchiveModal = () => {
    if (!archiveLoading) {
      setArchiveModal({ isOpen: false, locationId: null, locationName: '' });
      setErrorMessage('');
    }
  };

  const handleArchive = async () => {
    setArchiveLoading(true);
    setErrorMessage('');
    
    try {
      const response = await adminApi.patch(
        `/admin/locations/${archiveModal.locationId}/archive?archived=true`,
        {}
      );
      
      if (response.status === 200 || response.data.message) {
        closeArchiveModal();
        fetchLocations();
      }
    } catch (err) {
      console.error('Error archiving location:', err);
      
      // Handle different error formats
      let errorMsg = 'Failed to archive location. Please try again.';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        }
        // If detail is an object (single validation error)
        else if (typeof detail === 'object') {
          errorMsg = detail.message || detail.msg || JSON.stringify(detail);
        }
        // If detail is a string
        else {
          errorMsg = detail;
        }
      }
      
      setErrorMessage(errorMsg);
      // Don't close modal on error - let user see the error message
    } finally {
      setArchiveLoading(false);
    }
  };

  const openDeleteModal = (location) => {
    setDeleteModal({
      isOpen: true,
      locationId: location.id,
      locationName: location.name
    });
    setErrorMessage('');
  };

  const closeDeleteModal = () => {
    if (!deleteLoading) {
      setDeleteModal({ isOpen: false, locationId: null, locationName: '' });
      setErrorMessage('');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setErrorMessage('');
    
    try {
      const response = await adminApi.delete(
        `/admin/locations/${deleteModal.locationId}`
      );
      
      // Check if response is successful
      if (response.status === 200 || response.data.message) {
        closeDeleteModal();
        fetchLocations();
      }
    } catch (err) {
      console.error('Error deleting location:', err);
      
      // Handle different error formats
      let errorMsg = 'Failed to delete location. Please try again.';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        }
        // If detail is an object (single validation error)
        else if (typeof detail === 'object') {
          errorMsg = detail.message || detail.msg || JSON.stringify(detail);
        }
        // If detail is a string
        else {
          errorMsg = detail;
        }
      }
      
      setErrorMessage(errorMsg);
      // Don't close modal on error - let user see the error message
    } finally {
      setDeleteLoading(false);
    }
  };

  // QR Code Download Handler
  const handleDownloadQR = async (locationId, locationSlug) => {
    try {
      
      const response = await adminApi.get(
        `/admin/locations/${locationId}/qr-download`,
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-${locationSlug}.png`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setMessage({
        type: 'success',
        text: `QR code downloaded successfully!`
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      
    } catch (error) {
      console.error('Error downloading QR code:', error);
      setMessage({
        type: 'error',
        text: 'Failed to download QR code'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  // Row 74: a location with no qr_code_url used to show a permanent "Generating..."
  // with no way to tell a slow generation from a failed one. Give the admin the
  // same regenerate action the location detail page has.
  const handleGenerateQR = async (locationId) => {
    setGeneratingQR(locationId);
    try {
      await adminApi.post(`/admin/locations/${locationId}/regenerate-qr`, {});
      setMessage({ type: 'success', text: 'QR code generated.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      fetchLocations();
    } catch (error) {
      console.error('Error generating QR code:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to generate QR code' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setGeneratingQR(null);
    }
  };

  // QR Code Preview Handler
  const handlePreviewQR = (location) => {
    setQrPreview({
      location,
      imageUrl: resolveAssetUrl(location.qr_code_url),
      scanUrl: `https://tappd.app/scan?hub_id=${location.id}`
    });
  };

  const closePreview = () => {
    setQrPreview(null);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* V5 Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <p className="font-mono text-venue text-sm mb-1">// venues.hubs</p>
            <h1 className="font-display text-3xl text-navy">Hubs</h1>
            <p className="text-navy/50 mt-1">{total} total hubs (restaurants, bars, taprooms using Tappd)</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => navigate('/admin/locations/archived')}
              className="px-4 py-2.5 border border-navy/20 rounded-xl text-navy/70 hover:bg-white hover:border-navy/30 transition-all flex items-center gap-2"
            >
              <Archive size={18} />
              View Archived
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-venue to-teal-400 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-venue/20 transition-all flex items-center gap-2"
            >
              <Plus size={20} />
              Add Location
            </button>
          </div>
        </div>

        {/* V5 Search & Filters Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
          {/* Success/Error Message */}
          {message.text && (
            <div className={`mb-4 p-4 rounded-xl ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-navy/40" />
              <input
                type="text"
                placeholder="Search locations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy focus:outline-none focus:border-venue transition-colors"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        {/* Locations Table */}
        {/* V5 Locations Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-venue"></div>
          </div>
        ) : locations.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 py-20 text-center">
            <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="w-10 h-10 text-navy/20" />
            </div>
            <h3 className="font-display text-xl text-navy mb-2">No locations found</h3>
            <p className="text-navy/50">Try adjusting your search</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-cream/50 border-b border-navy/5">
                  <tr>
                    <th className="text-left p-4 font-body text-sm font-semibold text-navy/70">Location</th>
                    <th className="text-left p-4 font-body text-sm font-semibold text-navy/70">Address</th>
                    <th className="text-left p-4 font-body text-sm font-semibold text-navy/70">Contact</th>
                    <th className="text-left p-4 font-body text-sm font-semibold text-navy/70">Businesses</th>
                    <th className="text-left p-4 font-body text-sm font-semibold text-navy/70">QR Code</th>
                    <th className="text-left p-4 font-body text-sm font-semibold text-navy/70">Status</th>
                    <th className="text-left p-4 font-body text-sm font-semibold text-navy/70">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.map((location) => (
                    <tr key={location.id} className="border-t border-navy/5 hover:bg-cream/30 transition-colors">
                      <td className="p-4">
                        <div>
                          <p className="font-semibold text-navy flex items-center gap-2">
                            <Building2 size={16} className="text-venue" />
                            {location.name}
                          </p>
                          <p className="text-sm text-navy/50">
                            {location.address?.city || location.city}, {location.address?.state || location.state}
                          </p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-navy/70">
                          <div>{location.address?.street || location.address?.formatted || location.address || ''}</div>
                          <div>{location.address?.city || location.city}, {location.address?.state || location.state} {location.address?.zip_code || location.zip_code}</div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-navy/70">
                          {location.phone && <div>{location.phone}</div>}
                          {location.email && <div>{location.email}</div>}
                          {!location.phone && !location.email && <span className="text-navy/40">No contact</span>}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-venue/10 text-venue">
                          {location.total_businesses}
                        </span>
                      </td>
                      <td className="p-4">
                        {location.qr_code_url ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDownloadQR(location.id, location.slug)}
                              className="inline-flex items-center px-3 py-1.5 border border-navy/20 text-sm font-medium rounded-lg text-navy/70 bg-white hover:bg-cream transition-colors"
                              title="Download QR Code"
                            >
                              <Download size={16} className="mr-1.5" />
                              Download
                            </button>
                            <button
                              onClick={() => handlePreviewQR(location)}
                              className="p-2 rounded-lg hover:bg-cream transition-colors"
                              title="Preview QR Code"
                            >
                              <Eye size={18} className="text-navy/50" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleGenerateQR(location.id)}
                            disabled={generatingQR === location.id}
                            className="inline-flex items-center px-3 py-1.5 border border-navy/20 text-sm font-medium rounded-lg hover:bg-cream transition-colors disabled:opacity-50"
                          >
                            {generatingQR === location.id ? 'Generating…' : 'Generate QR'}
                          </button>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          location.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {location.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => navigate(`/admin/locations/${location.id}`)}
                            className="p-2 rounded-lg hover:bg-cream transition-colors"
                            title="View Details"
                          >
                            <Eye size={18} className="text-admin" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(location.id, location.is_active)}
                            className="p-2 rounded-lg hover:bg-cream transition-colors"
                            title={location.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {location.is_active ? (
                              <Pause size={18} className="text-consumer" />
                            ) : (
                              <Play size={18} className="text-green-600" />
                            )}
                          </button>
                          <button
                            onClick={() => openArchiveModal(location)}
                            className="p-2 rounded-lg hover:bg-cream transition-colors disabled:opacity-40"
                            title="Archive"
                            disabled={location.total_businesses > 0}
                          >
                            <Archive size={18} className={location.total_businesses > 0 ? 'text-navy/30' : 'text-consumer'} />
                          </button>
                          <button
                            onClick={() => openDeleteModal(location)}
                            className="p-2 rounded-lg hover:bg-cream transition-colors disabled:opacity-40"
                            title="Delete"
                            disabled={location.total_businesses > 0}
                          >
                            <Trash2 size={18} className={location.total_businesses > 0 ? 'text-navy/30' : 'text-red-500'} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingLocation) && (
        <LocationModal
          location={editingLocation}
          onClose={() => {
            setShowAddModal(false);
            setEditingLocation(null);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            setEditingLocation(null);
            fetchLocations();
          }}
        />
      )}

      {/* Archive Confirmation Modal */}
      <ConfirmModal
        isOpen={archiveModal.isOpen}
        onClose={closeArchiveModal}
        onConfirm={handleArchive}
        title="Archive Location"
        message={
          <div>
            <p className="mb-2">Are you sure you want to archive <strong>{archiveModal.locationName}</strong>?</p>
            <p className="text-sm text-orange-600">This location will be hidden from active lists but can be restored later. Make sure no active businesses are attached to this location.</p>
            {errorMessage && (
              <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">{errorMessage}</p>
              </div>
            )}
          </div>
        }
        confirmText="Archive Location"
        cancelText="Cancel"
        danger={false}
        loading={archiveLoading}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Location"
        message={
          <div>
            <p className="mb-2">Are you sure you want to delete <strong>{deleteModal.locationName}</strong>?</p>
            <p className="text-sm text-red-600">This action cannot be undone. Make sure no businesses are attached to this location.</p>
            {errorMessage && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
          </div>
        }
        confirmText="Delete Location"
        cancelText="Cancel"
        danger={true}
        loading={deleteLoading}
      />

      {/* QR Code Preview Modal */}
      {qrPreview && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            {/* V5 Modal Header */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-xl text-navy">
                QR Code - {qrPreview.location.name}
              </h3>
              <button
                onClick={closePreview}
                className="p-2 rounded-lg hover:bg-cream text-navy/50 hover:text-navy transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* QR Code Image */}
            <div className="flex justify-center mb-6 bg-cream p-8 rounded-xl">
              <img
                src={qrPreview.imageUrl}
                alt={`QR Code for ${qrPreview.location.name}`}
                className="w-64 h-64 border-2 border-navy/10 rounded-lg"
              />
            </div>
            
            {/* URL Display */}
            <div className="mb-4 p-4 bg-cream rounded-xl">
              <p className="text-sm text-navy/50 mb-1">Scans to:</p>
              <code className="text-sm font-mono text-navy">{qrPreview.scanUrl}</code>
            </div>
            
            {/* Stats (if available) */}
            {qrPreview.location.scan_count > 0 && (
              <div className="mb-4 p-4 bg-venue/10 rounded-xl border border-venue/20">
                <p className="text-sm text-venue">
                  📊 <strong>{qrPreview.location.scan_count}</strong> scans from this location
                </p>
              </div>
            )}
            
            {/* V5 Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => handleDownloadQR(qrPreview.location.id, qrPreview.location.slug)}
                className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-gradient-to-r from-venue to-teal-400 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-venue/20 transition-all"
              >
                <Download size={16} className="mr-2" />
                Download 2000x2000
              </button>
              <button
                onClick={closePreview}
                className="px-4 py-3 border border-navy/20 rounded-xl text-navy/70 font-medium hover:bg-cream transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Location Add/Edit Modal Component
const LocationModal = ({ location, onClose, onSuccess }) => {
  // Handle both old flat structure and new nested address structure
  const getAddressField = (field) => {
    if (location?.address && typeof location.address === 'object') {
      return location.address[field] || '';
    }
    return location?.[field] || '';
  };

  const [formData, setFormData] = useState({
    name: location?.name || '',
    slug: location?.slug || '',
    address: getAddressField('street') || getAddressField('formatted') || (typeof location?.address === 'string' ? location?.address : ''),
    city: getAddressField('city') || location?.city || '',
    state: getAddressField('state') || location?.state || '',
    zip_code: getAddressField('zip_code') || location?.zip_code || '',
    phone: location?.phone || '',
    email: location?.email || '',
    contact_first_name: location?.contact_first_name || '',
    contact_last_name: location?.contact_last_name || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (location) {
        // Update existing location
        await adminApi.put(`/admin/locations/${location.id}`, formData);
      } else {
        // Create new location
        await adminApi.post(`/admin/locations`, formData);
      }

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl my-8">
        <div className="p-8">
          <h2 className="font-display text-2xl text-navy mb-6">
            {location ? 'Edit Location' : 'Add New Location'}
          </h2>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-2">Location Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
                placeholder="e.g., Ott's"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-2">Slug * (URL-friendly identifier)</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                required
                className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
                placeholder="e.g., otts-medford"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-2">Street Address *</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-navy mb-2">City *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
                  placeholder="Medford"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">State *</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                  required
                  className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
                  placeholder="NJ"
                  maxLength="2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-2">ZIP Code *</label>
              <input
                type="text"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                required
                className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
                placeholder="08055"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-2">Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
                placeholder="(609) 555-0100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
                placeholder="contact@location.com"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Contact First Name</label>
                <input
                  type="text"
                  value={formData.contact_first_name}
                  onChange={(e) => setFormData({ ...formData, contact_first_name: e.target.value })}
                  className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Contact Last Name</label>
                <input
                  type="text"
                  value={formData.contact_last_name}
                  onChange={(e) => setFormData({ ...formData, contact_last_name: e.target.value })}
                  className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-venue transition-colors"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-navy/20 rounded-xl text-navy/70 font-medium hover:bg-cream transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-venue to-teal-400 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-venue/20 transition-all disabled:opacity-50"
              >
                {loading ? 'Saving...' : (location ? 'Update Location' : 'Create Location')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LocationManagement;
